import React, { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Icon from '@/components/Base/Icon';
import { useWindowDimensions } from '@/hooks';
import { laptopMin } from '@/styleUtils';

import {
  ContentLabel,
  ContentLabelInner,
  ContentLabelInnerText,
  ContentTooltip,
  IconWrap,
  Root,
} from './Tooltip.styles';

const MARGIN_FROM_VIEWPORT = 8;
const PADDING_LABEL = 8;
const TOOLTIP_ICON_WIDTH = 16;
const ANIMATION_TIME = 100;
const DEFAULT_ICON_NAME = 'tooltipIcon';
const DEFAULT_HORISONTAL_ALIGN = 'middle'; // oneOf(['left', 'middle', 'right', 'top'])

function getScrollbarWidth() {
  return window.innerWidth - document?.documentElement.clientWidth || 0;
}

function Tooltip({
  className = '',
  defaultIcon = false,
  icon = '',
  contentTooltip = '',
  contentLabel = '',
  mobileContentLabelDisabled = false,
  mlIconStyle = '0',
  topIconStyle = '',
  color = '',
  textColor = '',
  disabled = false,
  wrapper: ComponentLabelWrapper = null,
}) {
  const [isActive, setIsActive] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [horizontalAlign, setHorizontalAlign] = useState(
    DEFAULT_HORISONTAL_ALIGN
  );
  const [isHiddingAnimation, setIsHiddingAnimation] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < laptopMin;

  const isIcon = defaultIcon || icon;

  const rootRef = useRef(null);
  const contentTooltipRef = useRef(null);
  const clickAreaRef = useRef(null);

  const setTooltipLeft = (clickAreaCoords) => {
    contentTooltipRef.current.style.left = `${Math.floor(
      -clickAreaCoords.x + MARGIN_FROM_VIEWPORT
    )}px`;
  };

  const removeTooltipLeft = () => {
    if (contentTooltipRef.current?.style.left)
      contentTooltipRef.current.style.removeProperty('left');
  };

  const getCorrectHorizontalAlign = () => {
    const clickAreaCoords = clickAreaRef.current?.getBoundingClientRect();
    const tooltipCoords = contentTooltipRef.current?.getBoundingClientRect();

    if (clickAreaCoords && tooltipCoords) {
      const centerX =
        clickAreaCoords.x + clickAreaCoords.width - TOOLTIP_ICON_WIDTH / 2;

      if (
        tooltipCoords.width / 2 + MARGIN_FROM_VIEWPORT + scrollbarWidth >
        centerX
      ) {
        if (isMobile) setTooltipLeft(clickAreaCoords);
        if (!isMobile) removeTooltipLeft();

        return 'left';
      }

      if (
        tooltipCoords.width / 2 + MARGIN_FROM_VIEWPORT + centerX >
        windowWidth - scrollbarWidth
      ) {
        if (isMobile) setTooltipLeft(clickAreaCoords);
        if (!isMobile) removeTooltipLeft();

        return 'right';
      }
    }

    removeTooltipLeft();

    return DEFAULT_HORISONTAL_ALIGN;
  };

  const handleClose = () => {
    setIsHiddingAnimation(
      true,
      setTimeout(() => {
        setIsHiddingAnimation(false);
        setIsActive(false);
      }, ANIMATION_TIME)
    );
  };

  const handleCloseMobile = (event) => {
    if (rootRef.current && !rootRef.current.contains(event.target)) {
      handleClose();
      window.removeEventListener('click', handleCloseMobile);
      window.removeEventListener('touchstart', handleCloseMobile);
    }
  };

  useEffect(() => {
    if (!isActive) return;

    setHorizontalAlign(getCorrectHorizontalAlign);
  }, [isActive, windowWidth]);

  useEffect(
    () =>
      function clean() {
        window.removeEventListener('click', handleCloseMobile);
        window.removeEventListener('touchstart', handleCloseMobile);
      },
    []
  );

  useEffect(() => {
    setScrollbarWidth(getScrollbarWidth());
  }, []);

  const handleContentLabelMobile = () => {
    window.addEventListener('click', handleCloseMobile);
    window.addEventListener('touchstart', handleCloseMobile);
    setIsActive(!isActive);
  };

  const handleContentMouseEnter = () => {
    setIsActive(true);
  };

  const handleContentMouseLeave = () => {
    handleClose();
  };

  return (
    <Root
      ref={rootRef}
      className={className}
      onMouseLeave={!isMobile ? handleContentMouseLeave : undefined}
      $isActive={isActive}
      $isHiddingAnimation={isHiddingAnimation}
      data-state="tooltip"
    >
      <ContentLabel
        ref={clickAreaRef}
        data-state="tooltip-label"
        onClick={
          isMobile && !mobileContentLabelDisabled && !!contentTooltip
            ? handleContentLabelMobile
            : undefined
        }
        onMouseEnter={!isMobile ? handleContentMouseEnter : undefined}
      >
        <ContentLabelInner $contentLabelPadding={PADDING_LABEL}>
          <ContentLabelInnerText color={textColor} disabled={disabled}>
            {ComponentLabelWrapper ? (
              <ComponentLabelWrapper>{contentLabel}</ComponentLabelWrapper>
            ) : (
              contentLabel
            )}

            {!!contentTooltip && (
              <IconWrap
                data-state="tooltip-icon"
                onClick={
                  isMobile && mobileContentLabelDisabled
                    ? handleContentLabelMobile
                    : undefined
                }
                $mlIconStyle={mlIconStyle}
                $topIconStyle={topIconStyle}
                color={color}
                $isMobile={isMobile}
                onMouseEnter={!isMobile ? handleContentMouseEnter : undefined}
              >
                {isIcon && (
                  <Icon
                    width={`${TOOLTIP_ICON_WIDTH}`}
                    height="16"
                    name={icon || DEFAULT_ICON_NAME}
                  />
                )}

                {isActive && (
                  <ContentTooltip
                    className={classNames(
                      'contentTooltip',
                      horizontalAlign,
                      { active: isActive },
                      { hiddingAnimation: isHiddingAnimation }
                    )}
                    ref={contentTooltipRef}
                    $animationTime={ANIMATION_TIME}
                    $horizontalAlign={horizontalAlign}
                    $marginFromViewport={MARGIN_FROM_VIEWPORT}
                    $scrollbarWidth={scrollbarWidth}
                    $iconWidth={TOOLTIP_ICON_WIDTH}
                    data-state="tooltip-content"
                  >
                    {contentTooltip}
                  </ContentTooltip>
                )}
              </IconWrap>
            )}
          </ContentLabelInnerText>
        </ContentLabelInner>
      </ContentLabel>
    </Root>
  );
}

Tooltip.propTypes = {
  contentLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  contentTooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  mobileContentLabelDisabled: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.string,
  defaultIcon: PropTypes.bool,
  mlIconStyle: PropTypes.string,
  topIconStyle: PropTypes.string,
  color: PropTypes.string,
  textColor: PropTypes.string,
  disabled: PropTypes.bool,
  wrapper: PropTypes.node,
};

export default memo(Tooltip);
